<template>
  <div v-if="loading" class="flex h-full justify-center items-center">
    <loading-spinner diameter="64px" />
  </div>

  <alert-badge
    v-else-if="error.title"
    isDanger
    :title="error.title"
    :body="error.body"
    @dismissed="
      () => {
        error.title = ''
        error.body = ''
      }
    "
  />

  <div v-else class="flex flex-col md:flex-row">
    <div class="w-full mx-2 md:w-9/12">
      <div class="mt-2">
        <el-table
          :data="ir.inventoryReceivingItems"
          stripe
          border
          style="width: 100%"
          height="300"
          :max-height="800"
          class="card rounded"
          header-cell-class-name="my-header"
        >
          <el-table-column :min-width="40" type="index" label="#" />
          <el-table-column :min-width="90" prop="item.externalID" :label="t('itemCode')" />
          <el-table-column :min-width="220" :label="t('itemName')">
            <template #default="scope">
              <div class="truncate">
                {{ scope.row.item.name }}
              </div>
            </template>
          </el-table-column>
          <el-table-column :min-width="120" prop="costDinero" :label="t('cost')" align="center">
            <template #default="scope">
              <div class="flex justify-between">
                <span>
                  {{ formatDinero(scope.row.costDinero) }}
                </span>
              </div>
            </template>
          </el-table-column>

          <el-table-column :min-width="140" :label="t('costAfterExtras')" align="center">
            <template #default="scope">
              <div class="flex justify-between">
                <span>
                  {{ formatCostAfterExtras(scope.row) }}
                </span>
              </div>
            </template>
          </el-table-column>

          <el-table-column :min-width="120" :label="t('quantity')">
            <template #default="scope">
              <div class="flex items-center">
                <div class="px-2 pt-1">
                  {{ scope.row.quantity }}
                </div>
                <el-popover
                  v-if="scope.row.inventoryReceivingItemSerials.length"
                  placement="bottom-start"
                  :title="t('serialNumbers')"
                  :min-width="200"
                  trigger="hover"
                >
                  <template #default>
                    <p v-for="(serial, i) in scope.row.inventoryReceivingItemSerials" :key="i">
                      {{ serial.itemSerial.number }}
                    </p>
                  </template>
                  <template #reference>
                    <div class="flex justify-center">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        xmlns:xlink="http://www.w3.org/1999/xlink"
                        height="16px"
                        width="16px"
                        version="1.1"
                        id="Capa_1"
                        viewBox="0 0 15.273 15.273"
                        xml:space="preserve"
                      >
                        <g>
                          <path
                            style="fill: #030104"
                            d="M0,1.803h0.897V13.47H0V1.803z M1.446,13.47h0.505V1.803H1.446V13.47z M2.315,13.47h0.504V1.803   H2.315V13.47z M3.482,13.47h1.104V1.803H3.482V13.47z M5.47,13.47h0.552V1.803H5.47V13.47z M6.878,13.47h2.288V1.803H6.878V13.47z    M12.84,13.47h0.752V1.803H12.84V13.47z M14.248,1.803V13.47h1.025V1.803H14.248z M10.084,13.47h0.492V1.803h-0.492V13.47z    M11.281,13.47h0.492V1.803h-0.492V13.47z"
                          />
                        </g>
                      </svg>
                    </div>
                  </template>
                </el-popover>
              </div>
            </template>
          </el-table-column>
          <el-table-column :min-width="120" prop="extraProperties.batchNumber" :label="t('batchNumber')">
            <template #default="scope">
              <div>
                {{ scope.row.extraProperties?.batchNumber }}
              </div>
            </template>
          </el-table-column>
          <el-table-column :min-width="120" prop="extraProperties.expiryDate" :label="t('expiryDate')">
            <template #default="scope">
              <div>
                {{ scope.row.extraProperties?.expiryDate ? formatDate(scope.row.extraProperties?.expiryDate, 'DD-MM-YYYY') : '' }}
              </div>
            </template>
          </el-table-column>

          <el-table-column :min-width="120" prop="itemTotal" :label="t('itemTotal')" align="center">
            <template #default="scope">
              {{ calculateItemTotal(scope) }}
            </template>
          </el-table-column>

          <el-table-column :min-width="140" :label="t('totalAfterExtras')" align="center">
            <template #default="scope">
              {{ calculateTotalAfterExtras(scope.row) }}
            </template>
          </el-table-column>
        </el-table>
      </div>
      <div class="mt-4 font-bold">{{ t('relatedPayments') }}</div>
      <div class="mt-2">
        <el-table
          :data="ir.vendorBill.transactions.filter((t) => t.isReceipt).reverse()"
          stripe
          border
          style="width: 100%"
          :max-height="800"
          class="card rounded"
          header-cell-class-name="my-header2"
        >
          <el-table-column :min-width="60" prop="externalID" :label="t('paymentID')" />
          <el-table-column :min-width="90" prop="amountAmount" :label="t('amount')">
            <template #default="scope">
              <span :class="{ 'text-red-600': scope.row.receiptType == 'return' }">
                {{ formatDinero(scope.row.amountDinero) }}
              </span>
            </template>
          </el-table-column>
          <el-table-column :min-width="110" prop="createdAt" :label="t('date')">
            <template #default="scope">
              <span>
                {{ formatPostgresDate(scope.row.createdAt, 'DD/MM/YYYY hh:mm A') }}
              </span>
            </template>
          </el-table-column>
          <el-table-column :min-width="70" prop="externalID" :label="t('totalPaid')">
            <template #default="scope">
              <span :class="{ 'text-red-600': scope.row.receiptType == 'return' }">
                {{ formatDinero(scope.row.totalPaidAmountDinero) }}
              </span>
            </template>
          </el-table-column>
          <el-table-column :min-width="90" prop="externalID" :label="t('owed')">
            <template #default="scope">
              {{ formatDinero(getOwed(scope.row)) }}
            </template>
          </el-table-column>
          <el-table-column :min-width="100" :label="t('cashier')">
            <template #default="scope">
              <div class="flex items-center justify-around">
                <span>
                  {{ `${scope.row.user.name}` }}
                </span>
                <div>
                  <button
                    class="text-black hover:text-blue-600 font-bold"
                    @click="printTx(scope.row)"
                  >
                    <span class="fa fa-print"></span>
                  </button>
                </div>
              </div>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <!-- Related Vendor Bills Section -->
      <div class="mt-4 flex items-center gap-2">
        <span class="font-bold">{{ t('relatedVendorBills') }}</span>
        <button
          class="px-3 rounded-lg bg-blue-200 hover:bg-blue-300 active:bg-blue-400 text-xl"
          type="button"
          @click="openVendorBillDialog"
          :title="t('addVendorBill')"
        >
          +
        </button>
      </div>
      <div class="mt-2">
        <div v-if="loadingRelatedBills" class="text-center py-4">
          <loading-spinner diameter="32px" />
        </div>

        <el-table
          v-else-if="relatedVendorBillsData && relatedVendorBillsData.relatedVendorBills.length > 0"
          :data="[...relatedVendorBillsData.relatedVendorBills].reverse()"
          stripe
          border
          style="width: 100%"
          :max-height="800"
          class="card rounded"
          header-cell-class-name="my-header2"
        >
          <el-table-column :min-width="120" :label="t('vendorName')" prop="vendor.name">
            <template #default="scope">
              {{ scope.row.vendor?.name }}
            </template>
          </el-table-column>
          <el-table-column :min-width="100" :label="t('billNo')" prop="externalID">
            <template #default="scope">
              {{ scope.row.externalID }}
            </template>
          </el-table-column>
          <el-table-column :min-width="110" :label="t('billDate')" prop="createdAt">
            <template #default="scope">
              {{ formatPostgresDate(scope.row.createdAt, 'DD/MM/YYYY') }}
            </template>
          </el-table-column>
          <el-table-column :min-width="110" :label="t('dueDate')" prop="billDueDate">
            <template #default="scope">
              {{ scope.row.billDueDate ? formatPostgresDate(scope.row.billDueDate, 'DD/MM/YYYY') : '-' }}
            </template>
          </el-table-column>
          <el-table-column :min-width="100" :label="t('totalAmount')">
            <template #default="scope">
              {{ formatDinero(createDineroFromVendorBill(scope.row, 'totalDueAmount')) }}
            </template>
          </el-table-column>
          <el-table-column :min-width="100" :label="t('amountPaid')">
            <template #default="scope">
              {{ formatDinero(createDineroFromVendorBill(scope.row, 'totalPaidAmount')) }}
            </template>
          </el-table-column>
          <el-table-column :min-width="100" :label="t('balance')">
            <template #default="scope">
              {{ formatDinero(createDineroFromVendorBill(scope.row, 'totalBalanceAmount')) }}
            </template>
          </el-table-column>
          <el-table-column :min-width="80" :label="t('actions')">
            <template #default="scope">
              <button
                @click="viewVendorBill(scope.row.id)"
                class="text-blue-600 hover:text-blue-800"
                :title="t('viewVendorBill')"
              >
                <span class="fa fa-eye"></span>
              </button>
            </template>
          </el-table-column>
        </el-table>
      </div>
    </div>

    <div class="w-full md:w-3/12 mt-2" style="font-size: 14px !important">
      <div class="flex flex-col items-center px-3" v-if="ir">
        <div class="w-full">
          <div class="card rounded p-3">
            <div class="flex justify-between p-3 w-full rounded">
              <p>{{ t('vendorName') }}</p>
              <p class="flex justify-end custom-bold">
                {{ ir?.vendor?.name }}
              </p>
            </div>
            <div class="flex justify-between p-3 w-full bg-blue-100 rounded mt-1">
              <p>{{ t('vendorBillID') }}</p>
              <p class="flex justify-end custom-bold">
                {{ ir?.vendorBill?.externalID }}
              </p>
            </div>
            <div class="flex justify-between p-3 w-full bg-gray-100 rounded mt-1">
              <p>{{ t('receivingID') }}</p>
              <p class="flex justify-end custom-bold">
                {{ ir?.externalID }}
              </p>
            </div>

            <!-- Received On -->
            <div v-if="ir?.receivedOn" class="flex justify-between p-3 w-full mt-1">
              <p>{{ t('receivedOn') }}</p>
              <p class="flex justify-end custom-bold">
                {{ formatPostgresDate(ir?.receivedOn, 'DD/MM/YYYY') }}
              </p>
            </div>

            <!-- Vendor Bill Number -->
            <div v-if="ir?.vendorBillNumber" class="flex justify-between p-3 w-full mt-1">
              <p>{{ t('vendorBillNumber') }}</p>
              <p class="flex justify-end custom-bold">
                {{ ir?.vendorBillNumber }}
              </p>
            </div>

            <!-- Created At -->
            <div class="flex justify-between p-3 w-full mt-1 bg-gray-50 rounded">
              <p>{{ t('models.common.createdAt') }}</p>
              <p class="flex justify-end custom-bold">
                {{ formatPostgresDate(ir?.createdAt, 'DD/MM/YYYY hh:mm A') }}
              </p>
            </div>

            <div class="flex justify-between p-3 w-full">
              <p>{{ t('receivedBy') }}</p>
              <p class="flex justify-end custom-bold">
                {{ ir?.user.fullName }}
              </p>
            </div>
            <div class="flex justify-between w-full p-3">
              <p>{{ t('location') }}</p>
              <p class="flex justify-end custom-bold">
                {{ $ctx.locale === 'en' ? ir?.location?.enName : ir?.location?.arName }}
              </p>
            </div>
            <div class="flex justify-between w-full p-3">
              <p>{{ t('notes') }}</p>
              <p class="flex justify-end custom-bold">
                {{ ir?.notes }}
              </p>
            </div>
          </div>

          <div class="card rounded p-3 mt-4">
            <div class="flex justify-between w-full p-3 rounded">
              <p>{{ t('totalBeforeExtras') }}</p>
              <p class="flex justify-end custom-bold">
                {{ formatDinero(getTotalBeforeExtras()) }}
              </p>
            </div>
            <div class="flex justify-between w-full p-3 rounded mt-1">
              <p>{{ t('otherCosts') }}</p>
              <p class="flex justify-end custom-bold">
                {{ formatDinero(ir.vendorBill?.otherCostsDinero) }}
              </p>
            </div>
            <div class="flex justify-between w-full p-3 rounded mt-1">
              <p>{{ t('total') }}</p>
              <p class="flex justify-end custom-bold">
                {{ formatDinero(ir.vendorBill?.totalDueDinero) }}
              </p>
            </div>
            <div class="flex justify-between w-full p-3 bg-gray-100 rounded mt-2">
              <p>{{ t('moneyOwed') }}</p>
              <p class="flex justify-end custom-bold">
                {{ formatDinero(ir.vendorBill?.totalDueDinero) }}
              </p>
            </div>
            <div class="flex justify-between w-full p-3 bg-green-100 rounded mt-2">
              <p>{{ t('paymentsMade') }}</p>
              <p class="flex justify-end custom-bold">
                {{ formatDinero(ir.vendorBill?.totalPaidDinero) }}
              </p>
            </div>
            <div class="flex justify-between w-full p-3 bg-red-100 rounded mt-2">
              <p>{{ t('remainingBalance') }}</p>
              <p class="flex justify-end custom-bold">
                {{ formatDinero(ir.vendorBill?.totalBalanceDinero) }}
              </p>
            </div>
          </div>
        </div>

        <div class="card rounded w-full p-3 mt-4">
          <div class="flex flex-col items-center w-12/12 gap-1 text-black">
            <horizontal-button
              faIcon="print"
              :title="t('printReceipt')"
              @click="$router.push($Route.INVENTORY_INVENTORY_RECEIVINGS_RECEIPT.replace(':id', ir.id))"
            />
            <horizontal-button
              faIcon="money-bill-wave"
              :title="t('makePayment')"
              @click="handleMakePayment()"
            />
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Create Vendor Bill Dialog -->
  <create-vendor-bill-dialog
    v-if="vendorBillDialogOpen"
    v-model="vendorBillDialogOpen"
    :inventoryReceivingID="route.params.id as string"
    :currency="relatedVendorBillsData?.currency || ir.vendorBill?.currency"
    :precision="relatedVendorBillsData?.precision || ir.vendorBill?.precision"
    :vendor="ir.vendor"
    :debitedAccount="null"
    :key="vendorBillDialogKey"
    @submit="handleCreateVendorBill"
  />
</template>

<script lang="ts">
import { defineComponent, onBeforeMount, ref } from 'vue'

import { useHTTP } from '@/plugins/http'
import { ElMessage } from 'element-plus'

// components
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import AlertBadge from '@/components/AlertBadge.vue'
import CreateVendorBillDialog from '@/components/inventory/CreateVendorBillDialog.vue'
import InventoryReceiving, {
  inventoryReceivingI18nMessages
} from '@/models/inventory/InventoryReceiving'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

// helpers
import { formatDinero } from '@/utils/money'
import { formatPostgresDate, formatDate } from '@/utils/date'
import Dinero, { Currency } from 'dinero.js'
import HorizontalButton from '@/components/HorizontalButton.vue'
import { Route } from '@/router/routes'
import { i18nOrderMessages } from '@/models/sales/Order'
import { itemI18nMessages } from '@/models/inventory/Item'
import { vendorBillI18nMessages } from '@/models/vendor/VendorBill'

export default defineComponent({
  name: 'inventory-receiving',
  components: {
    LoadingSpinner,
    AlertBadge,
    HorizontalButton,
    CreateVendorBillDialog
  },
  setup() {
    const { t } = useI18n({
      messages: {
        en: {
          ...vendorBillI18nMessages.en,
          ...inventoryReceivingI18nMessages.en,
          ...i18nOrderMessages.en,
          ...itemI18nMessages.en,
          generalInfoSection: 'General Info',
          label: 'Key',
          value: 'Value',
          receivingID: 'Receiving ID',
          receivedBy: 'Received By',
          paymentsMade: 'Payments Made',
          remainingBalance: 'Remaining Balance',
          printReceipt: 'Print Receipt',
          makePayment: 'Make Payment',
          moneyOwed: 'Money Owed',
          relatedPayments: 'Related Payments',
          owed: 'Owed',
          date: 'Date',
          paymentID: 'Payment ID',
          amount: 'Amount',
          cashier: 'Cashier',
          batchNumber: 'Batch Number',
          expiryDate: 'Expiry Date',
          manufactureDate: 'Manufacture Date',
          relatedVendorBills: 'Related Vendor Bills',
          addVendorBill: 'Add Vendor Bill',
          mainBill: 'Main',
          viewVendorBill: 'View Vendor Bill',
          vendorBillCreated: 'Vendor bill created successfully',
          errorCreatingVendorBill: 'Failed to create vendor bill',
          noAdditionalVendorBills: 'No additional vendor bills. Click + to add customs, shipping, or other fees.',
          vendorName: 'Vendor',
          billNo: 'Bill #',
          billDate: 'Bill Date',
          dueDate: 'Due Date',
          totalAmount: 'Total',
          amountPaid: 'Amount Paid',
          balance: 'Balance',
          actions: 'Actions',
          vendorBillNumber: 'Vendor Bill Number',
          totalBeforeExtras: 'Total (before extras)',
          otherCosts: 'Other Costs',
          total: 'Total',
          costAfterExtras: 'Cost after Extras',
          totalAfterExtras: 'Total after Extras'
        },
        ar: {
          ...vendorBillI18nMessages.ar,
          ...inventoryReceivingI18nMessages.ar,
          ...i18nOrderMessages.ar,
          ...itemI18nMessages.ar,
          generalInfoSection: 'المعلومات العامة',
          label: 'الحقل',
          value: 'القيمة',
          paymentID: 'رقم الحركة',

          receivingID: 'رقم الاستلام',
          receivedBy: 'استلم من قبل',
          remainingBalance: 'المبلغ المتبقي',
          printReceipt: 'طباعة الإيصال',
          makePayment: 'تسديد مبلغ',
          relatedPayments: 'المدفوعات ذات الصلة',
          amount: 'القيمة',

          paymentsMade: 'المبلغ المدفوع',
          moneyOwed: 'المبلغ المطلوب',
          owed: 'الباقي',
          date: 'التاريخ',
          cashier: 'المحاسب',
          batchNumber: 'رقم الوجبة',
          expiryDate: 'تاريخ الإنتهاء',
          manufactureDate: 'تاريخ الصنع',
          relatedVendorBills: 'الفواتير المرتبطة بطلب الشراء',
          addVendorBill: 'إضافة فاتورة مورد',
          mainBill: 'رئيسي',
          viewVendorBill: 'عرض فاتورة المورد',
          vendorBillCreated: 'تم إنشاء فاتورة المورد بنجاح',
          errorCreatingVendorBill: 'فشل إنشاء فاتورة المورد',
          noAdditionalVendorBills: 'لا توجد فواتير مورد إضافية. انقر + لإضافة الرسوم الجمركية أو الشحن أو الرسوم الأخرى.',
          vendorName: 'المورد',
          billNo: 'رقم الفاتورة',
          billDate: 'تاريخ الفاتورة',
          dueDate: 'تاريخ الاستحقاق',
          totalAmount: 'المجموع',
          amountPaid: 'المبلغ المدفوع',
          balance: 'الرصيد',
          actions: 'العمليات',
          vendorBillNumber: 'رقم فاتورة المورّد',
          totalBeforeExtras: 'المجموع (قبل الإضافات)',
          otherCosts: 'التكاليف الأخرى',
          total: 'المجموع',
          costAfterExtras: 'الكلفة بعد الإضافات',
          totalAfterExtras: 'المجموع بعد الإضافات'
        }
      }
    })

    const ir = ref(new InventoryReceiving())
    const http = useHTTP()
    const loading = ref(true)
    const error = ref({ title: '', body: '' })
    const route = useRoute()
    const router = useRouter()

    // Related Vendor Bills state
    const relatedVendorBillsData = ref<any>(null)
    const loadingRelatedBills = ref(false)
    const vendorBillDialogOpen = ref(false)
    const vendorBillDialogKey = ref(0)

    async function loadInventoryReceiving() {
      const url = `${InventoryReceiving.ENDPOINT}/${route.params.id}`

      try {
        const resp = await http.get<InventoryReceiving>(url)
        ir.value = InventoryReceiving.from(resp)
      } catch (errorResp: any) {
        error.value.title = errorResp.title
        error.value.body = errorResp.body
      }
    }

    onBeforeMount(async () => {
      try {
        await loadInventoryReceiving()
        // Fetch related vendor bills
        await fetchRelatedVendorBills()
      } catch (errorResp: any) {
        error.value.title = errorResp.title
        error.value.body = errorResp.body
      }

      loading.value = false
    })

    async function fetchRelatedVendorBills() {
      loadingRelatedBills.value = true
      try {
        const url = `${InventoryReceiving.ENDPOINT}/${route.params.id}/related-vendor-bills`
        const resp = await http.get<any>(url)
        relatedVendorBillsData.value = resp
      } catch (err) {
        console.error('Error fetching related vendor bills:', err)
      } finally {
        loadingRelatedBills.value = false
      }
    }

    function handleMakePayment() {
      router.push(
        Route.ACCOUNTING_VENDORS_BILL_PAYMENT.replace(':id', ir.value.vendorBillID as string)
      )
    }

    function getOwed(row: any) {
      let owed = 0
      owed = row.totalDueAmount > 0 ? row.totalDueAmount - row.amountAmount : 0

      const value = Dinero({
        amount: owed,
        currency: row.currency,
        precision: row.precision
      })

      return value
    }

    function printTx(data: any) {
      router.push(Route.INVENTORY_INVENTORY_RECEIVINGS_RECEIPT.replace(':id', inventoryReceiving.value.id as string) + `?transactionID=${data.id}`)
    }

    function calculateItemTotal(scope: any) {
      const price = Dinero({
        amount: scope.row.quantity * scope.row.costAmount,
        currency: scope.row.item.costCurrency,
        precision: scope.row.item.costPrecision
      })
      return formatDinero(price)
    }

    function formatCostAfterExtras(row: any) {
      if (!row.extraProperties?.costAfterExtras) {
        return '-'
      }
      const price = Dinero({
        amount: row.extraProperties.costAfterExtras,
        currency: 'IQD',
        precision: 0
      })
      return formatDinero(price)
    }

    function calculateTotalAfterExtras(row: any) {
      if (!row.extraProperties?.costAfterExtras) {
        return '-'
      }
      const price = Dinero({
        amount: row.quantity * row.extraProperties.costAfterExtras,
        currency: 'IQD',
        precision: 0
      })
      return formatDinero(price)
    }

    function openVendorBillDialog() {
      vendorBillDialogOpen.value = true
    }

    function viewVendorBill(vendorBillId: string) {
      router.push(Route.ACCOUNTING_VENDORS_BILL_VIEW.replace(':id', vendorBillId))
    }

    async function handleCreateVendorBill(formData: any) {
      try {
        const url = `${InventoryReceiving.ENDPOINT}/${route.params.id}/related-vendor-bills`

        // Create FormData for multipart upload
        const multipartData = new FormData()

        // Add bill data as JSON body
        const billData = {
          vendorID: formData.vendorID,
          billDate: formData.billDate,
          billDueDate: formData.billDueDate,
          vendorBillNumber: formData.vendorBillNumber,
          totalDueAmount: formData.totalDueAmount,
          totalPaidAmount: formData.totalPaidAmount,
          currency: formData.currency,
          precision: formData.precision,
          requestOnlyPayFromAccountID: formData.requestOnlyPayFromAccountID,
          whatsFor: formData.whatsFor,
          notes: formData.notes
        }
        multipartData.set('body', JSON.stringify(billData))

        // Add attachment files if any
        if (formData.attachmentFiles && formData.attachmentFiles.length > 0) {
          formData.attachmentFiles.forEach((file: any) => {
            multipartData.append('attachments', file)
          })
        }

        await http.post(url, multipartData)

        // Refresh inventory receiving data (includes costAfterExtras)
        await loadInventoryReceiving()

        // Refresh related vendor bills
        await fetchRelatedVendorBills()

        // Close dialog on success
        vendorBillDialogOpen.value = false

        // Show success message
        ElMessage.success(t('vendorBillCreated'))
      } catch (err: any) {
        console.error('Error creating vendor bill:', err)
        // Show error message - don't close dialog
        ElMessage.error(err?.title || err?.message || t('errorCreatingVendorBill'))
        // Force re-render dialog to reset isSubmitting state
        vendorBillDialogKey.value++
      }
    }

    function createDineroFromVendorBill(vendorBill: any, field: 'totalDueAmount' | 'totalPaidAmount' | 'totalBalanceAmount') {
      return Dinero({
        amount: vendorBill[field] || 0,
        currency: vendorBill.currency as Currency,
        precision: vendorBill.precision || 0
      })
    }

    function createDineroFromAmount(amount: number, currency: string, precision: number) {
      return Dinero({
        amount: amount || 0,
        currency: currency as Currency,
        precision: precision || 0
      })
    }

    function getTotalBeforeExtras() {
      const vendorBill = ir.value.vendorBill
      if (!vendorBill?.totalDueDinero || !vendorBill?.otherCostsDinero) {
        return Dinero({
          amount: 0,
          currency: (vendorBill?.currency || 'IQD') as Currency,
          precision: vendorBill?.precision || 0
        })
      }
      return vendorBill.totalDueDinero.subtract(vendorBill.otherCostsDinero)
    }

    return {
      ir,
      loading,
      error,
      t,
      formatPostgresDate,
      formatDinero,
      handleMakePayment,
      printTx,
      getOwed,
      calculateItemTotal,
      formatCostAfterExtras,
      calculateTotalAfterExtras,
      formatDate,
      relatedVendorBillsData,
      loadingRelatedBills,
      vendorBillDialogOpen,
      openVendorBillDialog,
      viewVendorBill,
      handleCreateVendorBill,
      fetchRelatedVendorBills,
      createDineroFromVendorBill,
      createDineroFromAmount,
      getTotalBeforeExtras,
      vendorBillDialogKey,
      route
    }
  }
})
</script>

<style scoped>
/* table */
table {
  font-family: arial, sans-serif;
  border-collapse: collapse;
}
td,
th {
  border: 1px solid #dddddd;
  text-align: center;
  padding: 4px;
}
tr:nth-child(odd) {
  background-color: #ebebeb;
}
</style>
